/** Thin REST client for the Worker API. All shapes validated with shared Zod. */
import {
  createGameResponseSchema,
  gameSnapshotSchema,
  gameSummarySchema,
  practiceGameResponseSchema,
  publicGamesResponseSchema,
  sessionResponseSchema,
  type CreateGameRequest,
  type CreateGameResponse,
  type GameSnapshot,
  type GameSummary,
  type PracticeGameResponse,
  type SessionResponse,
} from "@msf/shared";
import { z } from "zod";

const BASE = "/api";

export class ApiError extends Error {
  constructor(
    public code: string,
    public status: number,
  ) {
    super(code);
  }
}

async function request<T>(path: string, opts: RequestInit, schema: z.ZodType<T>, token?: string): Promise<T> {
  const headers = new Headers(opts.headers);
  headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`${BASE}${path}`, { ...opts, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = (data as { error?: string }).error ?? `http_${res.status}`;
    throw new ApiError(code, res.status);
  }
  return schema.parse(data);
}

export const api = {
  createSession(nickname: string): Promise<SessionResponse> {
    return request("/session", { method: "POST", body: JSON.stringify({ nickname }) }, sessionResponseSchema);
  },
  updateNickname(nickname: string, token: string): Promise<SessionResponse> {
    return request(
      "/session",
      { method: "PATCH", body: JSON.stringify({ nickname }) },
      sessionResponseSchema,
      token,
    );
  },
  createGame(req: CreateGameRequest, token: string): Promise<CreateGameResponse> {
    return request("/games", { method: "POST", body: JSON.stringify(req) }, createGameResponseSchema, token);
  },
  createPracticeGame(token: string): Promise<PracticeGameResponse> {
    return request("/games/practice", { method: "POST", body: "{}" }, practiceGameResponseSchema, token);
  },
  listPublicGames(): Promise<GameSummary[]> {
    return request("/games", { method: "GET" }, publicGamesResponseSchema).then((r) => r.games);
  },
  resolveCode(code: string): Promise<GameSummary> {
    return request(`/games/resolve/${encodeURIComponent(code)}`, { method: "GET" }, gameSummarySchema);
  },
  getGame(id: string, token?: string): Promise<GameSnapshot> {
    return request(`/games/${encodeURIComponent(id)}`, { method: "GET" }, gameSnapshotSchema, token);
  },
  joinGame(id: string, token: string, pin?: string): Promise<{ team: "red" | "blue"; status: string }> {
    return request(
      `/games/${encodeURIComponent(id)}/join`,
      { method: "POST", body: JSON.stringify(pin ? { pin } : {}) },
      z.object({ team: z.enum(["red", "blue"]), status: z.string() }),
      token,
    );
  },
};
